import { CallState, ElioError, getError, toElioError } from '@elio/shared/models';
import { nonFlickerLoader } from '@elio/shared/rxjs-utils';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of, switchMap } from 'rxjs';

export interface CustomComponentState {
  callState: CallState;
}

export const customInitialState: CustomComponentState = {
  callState: 'INIT',
};

export class CustomComponentStore<
  U extends object | void = void,
  T extends (U & CustomComponentState) | CustomComponentState = U extends void
    ? CustomComponentState
    : U & CustomComponentState,
> extends ComponentStore<T> {
  constructor(initialState: U = {} as U) {
    super({ ...customInitialState, ...initialState } as T);
  }

  readonly isLoading$: Observable<boolean> = this.select((state) => state.callState === 'LOADING');

  readonly isLoadingWithFlicker$: Observable<boolean> = this.select((state) => state.callState === 'LOADING').pipe(
    switchMap((loading) => nonFlickerLoader(of(loading)))
  );

  readonly isLoaded$: Observable<boolean> = this.select((state) => state.callState === 'LOADED').pipe(
    switchMap((loaded) => of(loaded))
  );

  readonly callState$ = this.select((state) => state.callState);

  readonly error$: Observable<string | undefined> = this.select((state) => getError(state.callState)?.messageKey);

  readonly updateCallState = this.updater((state: T, callState: CallState | undefined): T => {
    return {
      ...state,
      callState: callState ?? 'LOADED',
    };
  });

  readonly startLoading = this.updater((state: T, patchedState: Partial<U> | void): T => {
    return {
      ...state,
      ...patchedState,
      callState: 'LOADING',
    };
  });

  readonly stopLoading = this.updater((state: T, patchedState: Partial<U> | void): T => {
    return {
      ...state,
      ...patchedState,
      callState: 'LOADED',
    };
  });

  protected handleError(error: unknown, patchedState: Partial<U> = {}): ElioError {
    const elioError = toElioError(error);
    this.patchState({
      callState: { error: elioError },
      ...patchedState,
    } as Partial<T>);
    return elioError;
  }
}
